import React, { useState, useRef, useMemo, useEffect } from 'react';
import moment from 'moment';
import Modal from 'react-modal';
import Alert from '@components/Widgets/Alert/Alert.react';
import Analytics from '@utils/Analytics';
import { getParticipantsInfo, getPrimaryMeal, dateCompare, normalizeParticipantsToArray, getParticipantsForSpecificMeal } from '@utils/Meals';
import { isSunbasketFood } from '@utils/Sunbasket';
import { calculateTotalServingsForParticipants, getBaseServings, calculateRequiredScaling } from '@utils/servingsCalculations';
import {
    buildConsumerLeftoverSlots,
    isParticipantChecked,
    areAllParticipantsSelected,
    toggleParticipantSelection,
    toggleAllParticipants,
    updateGuestCount,
    getGuestCount,
    findConflictingMeals,
    buildLeftoverSchedulerProps,
    getLeftoverSchedulerMode
} from '@utils/leftoverUtils';
import './LeftoverDatesModal.scss';

// Separates this funnel from the PRO editor's offset-keyed twin.
const ANALYTICS_CONTEXT = 'Consumer Planner';

// The "logged" rule of the past Meal Feed card (areMealsLogged, #2544): every non-deleted fresh,
// leftover or food dish of the slot is logged, and the slot has at least one. A copy until #2544 is in master.
const isSlotLogged = (slotMeals) => {
    const edibleMeals = slotMeals.filter((m) => ['fresh', 'leftover', 'food'].includes(m?.meal_type) && !m.deleted);

    return edibleMeals.length > 0 && edibleMeals.every((m) => m.logged_portion);
};

const LeftoverDatesModal = ({ user, meals, recipes, foods, plans, leftoverParams, ctaTitle = 'Save', closeModal, onSelectLeftoverOffsets, rescheduleMealParams, rescheduleMeals, returnMode = false, onReturnLeftoverData, onShowConflictModal }) => {
    const { leftovers, meal, extraServings: mealExtraServings } = leftoverParams;

    // Build available slots starting from next meal slot after main meal
    const availableSlots = buildConsumerLeftoverSlots(meal, plans, user, rescheduleMealParams);

    const participantDetails = getParticipantsInfo(user);

    // A meal being added has no uuid yet, so it cannot own any leftovers. Guard on that
    // before filtering: a loose `==` here matched every meal with a null parent_uuid
    // (undefined == null), preselecting every main meal in the plan.
    let oldLeftovers = meal.uuid
        ? meals.filter((m) => m.parent_uuid === meal.uuid).sort(dateCompare)
        : [];

    let defaultSelected = oldLeftovers.map((m) => {
        const mealWithArrayParticipants = {
            ...m,
            participants: normalizeParticipantsToArray(m.participants)
        };

        return {
            date: m.date,
            mealType: m.meal,
            participants: getParticipantsForSpecificMeal(mealWithArrayParticipants, user)
        };
    });

    if (leftovers) {
        oldLeftovers = leftovers;
        defaultSelected = oldLeftovers.map((m, index) => ({
            date: moment(m.leftoverDate).format('YYYY-MM-DD'),
            mealType: m.mealType,
            participants: m.participants
        }));
    }

    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => defaultSelected.length && !rescheduleMealParams ? defaultSelected : []);
    const [extraServings, setExtraServings] = useState((mealExtraServings || 0));
    const [expandedSlots, setExpandedSlots] = useState({});
    const initialSelectedRef = useRef(defaultSelected.length && !rescheduleMealParams ? defaultSelected : []);

    const getScalingMap = () => {
        const dishServings  = getTotalServings();
        let scalingMap = {};

        dishServings.forEach(({uuid, scaling}) => {
            scalingMap[uuid] = scaling;
        });

        return scalingMap;
    }

    const getDishes = () => {
        let contents = {};

        const mealDishes = meals.filter(m => m?.meal === meal.meal && m?.date === meal.date && !m?.deleted && (recipes[m?.recipe_uuid]?.servings || isSunbasketFood(foods[m?.food_uuid])));

        mealDishes.forEach((dish) => {
            if(recipes[dish.recipe_uuid]?.servings) {
                contents[dish.recipe_uuid] = recipes[dish.recipe_uuid]
            }

            if(foods[dish.food_uuid]?.servings) {
                contents[dish.food_uuid] = foods[dish.food_uuid]
            }
        });

        const dishes= [];
        const addedUuids = new Set(); // Track which dishes we've already added to avoid duplicates

        // First, add dishes from database meals
        mealDishes.forEach(meal => {
            let dish = Object.assign({}, meal);
            if (dish.meal_type !== 'leftover') {
                const leftovers = meals.filter(item => item.parent_uuid === dish.uuid && !item.deleted);

                dish = { ...dish, main: meal, leftovers: leftovers };
                dishes.push(dish);
                addedUuids.add(dish.uuid || dish.id);
            }
        });

        // Then, sync with modifiedDishes passed from EditMealServingsModalPrimary.
        // These reflect unsaved in-modal changes (e.g. updated logged_amount, scaling)
        // that haven't been persisted to the DB yet, so they must override the stale DB dishes above.
        if (leftoverParams?.modifiedDishes && Array.isArray(leftoverParams.modifiedDishes)) {
            leftoverParams.modifiedDishes.forEach(modifiedDish => {
                const mainMeal = modifiedDish.main;
                if (mainMeal && mainMeal.meal_type !== 'leftover') {
                    const dishId = mainMeal.uuid || mainMeal.id;

                    // Always ensure contents has the latest recipe/food for this dish
                    if (mainMeal.recipe_uuid && recipes[mainMeal.recipe_uuid]?.servings) {
                        contents[mainMeal.recipe_uuid] = recipes[mainMeal.recipe_uuid];
                    }
                    if (mainMeal.food_uuid && foods[mainMeal.food_uuid]?.servings) {
                        contents[mainMeal.food_uuid] = foods[mainMeal.food_uuid];
                    }

                    if (addedUuids.has(dishId)) {
                        // Dish was already added from DB above — replace its .main with the
                        // modifiedDish version so downstream calculations (actualYield,
                        // totalServings via getTotalServings) use the updated values.
                        const existingIndex = dishes.findIndex(d => (d.main?.uuid || d.main?.id) === dishId);
                        if (existingIndex !== -1) {
                            dishes[existingIndex] = {
                                ...dishes[existingIndex],    // keep any other fields (e.g. top-level uuid)
                                main: mainMeal,              // override .main with updated dish data
                                leftovers: modifiedDish.leftovers || dishes[existingIndex].leftovers || []
                            };
                        }
                    } else {
                        // Dish not yet in the array (e.g. newly added in EditMealServings) — just push it
                        dishes.push({
                            main: mainMeal,
                            leftovers: modifiedDish.leftovers || []
                        });
                        addedUuids.add(dishId);
                    }
                }
            });
        }

        return { dishes, contents };
    }

    const getTotalServings = ({ dishes, contents } = getDishes()) => {

        return dishes.map(({main: dish}) => {
            const dishContent = contents[dish.recipe_uuid || dish.food_uuid];

            // Get all participants: main meal + all selected leftovers
            const mainMealParticipants = getParticipantsForSpecificMeal(dish, user);
            const leftoverParticipants = selected.flatMap(selection => selection.participants);
            const allParticipants = [...mainMealParticipants, ...leftoverParticipants];

            // Use utility to calculate total servings (handles USER logged servings)
            const totalServings = calculateTotalServingsForParticipants(allParticipants, dish, dishContent);

            const baseServings = getBaseServings(dishContent);
            const scaling = calculateRequiredScaling(totalServings + extraServings, baseServings);
            const totalYield = scaling * baseServings;
            const uuid = dish.uuid;
            const title = contents[dish.recipe_uuid]?.title || contents[dish.food_uuid]?.name;

            return {uuid, title, totalYield, totalServings, totalLeftoverServings: totalServings, scaling};
        });
    }

    const isChecked = (date, mealTypeName, participantUuid) => {
        return isParticipantChecked(selected, 'date', date, mealTypeName, participantUuid);
    }

    const allParticipantsSelected = (date, mealTypeName) => {
        const match = selected.find(item => item.date === date && item.mealType === mealTypeName);
        return areAllParticipantsSelected(match, participantDetails);
    }

    const toggleCheckbox = (date, mealTypeName, participantUuid) => {
        setSelected(prev => toggleParticipantSelection(prev, 'date', date, mealTypeName, participantUuid, participantDetails));
    };

    const selectAllParticipants = (date, mealTypeName) => {
        setSelected(prev => toggleAllParticipants(prev, 'date', date, mealTypeName, participantDetails));
    }

    const onChangeNumberOfGuests = (date, mealTypeName, newGuestCount) => {
        setSelected(prev => updateGuestCount(prev, 'date', date, mealTypeName, newGuestCount, 'Guest '));
    };

    const onChangeParticipantPortion = (date, mealTypeName, participantUuid, newPortion) => {
        setSelected(prev => {
            const match = prev.find(item => item.date === date && item.mealType === mealTypeName);
            const selectedWithoutMatch = prev.filter(item => !(item.date === date && item.mealType === mealTypeName));

            // If participant is not checked, check them first
            if (!match) {
                const addParticipant = participantDetails.find((p) => p.uuid === participantUuid);
                const newParticipant = { ...addParticipant, portion: Math.max(0.25, newPortion) };
                return [...prev, { date, mealType: mealTypeName, participants: [newParticipant] }];
            }

            // Update the portion
            const updatedParticipants = match.participants.map(p => {
                if (p.uuid === participantUuid) {
                    return { ...p, portion: Math.max(0.25, newPortion) };
                }
                return p;
            });

            return [...selectedWithoutMatch, { date, mealType: mealTypeName, participants: updatedParticipants }];
        });
    };

    const getCurrentGuestCount = (date, mealTypeName) => {
        return getGuestCount(selected, 'date', date, mealTypeName);
    };

    const clearAll = () => {
        Analytics.clearLeftoverSelections({
            'Context': ANALYTICS_CONTEXT,
            'Slots Cleared': selected.length,
            'Extra Servings Cleared': extraServings,
        });

        setSelected([]);
        setExtraServings(0);
    }

    const toggleSlotExpand = (date, mealTypeName) => {
        const key = `${date}_${mealTypeName}`;
        setExpandedSlots(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getSelectedCount = (date, mealTypeName) => {
        const match = selected.find(item => item.date === date && item.mealType === mealTypeName);
        return match ? match.participants.length : 0;
    };

    const onClickCta = () => {
        trackCtaClick();

        if(rescheduleMealParams) {
            const userLeftovers = selected.map(({date, mealType, participants}) => {
                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants};
            });
            rescheduleMeals(rescheduleMealParams.meals, rescheduleMealParams.date, rescheduleMealParams.mealType, userLeftovers);
        } else if (returnMode && onReturnLeftoverData) {
            // Return mode: send data back to parent instead of applying directly
            const leftovers = selected.map(({date, mealType, participants}) => {
                // Check if this leftover already exists with same date and mealType
                // If only participants changed, it's not a conflict
                const isExistingLeftover = oldLeftovers.some(oldLeftover => {
                    if (oldLeftover.date && oldLeftover.meal) {
                        return oldLeftover.date === date && oldLeftover.meal === mealType;
                    }
                    if (oldLeftover.leftoverDate && oldLeftover.mealType) {
                        const oldDate = moment(oldLeftover.leftoverDate).format('YYYY-MM-DD');
                        return oldDate === date && oldLeftover.mealType === mealType;
                    }
                    return false;
                });

                // Find conflicting meals only if this is a NEW leftover (date/mealType changed)
                const conflictingMeals = isExistingLeftover
                    ? []
                    : findConflictingMeals(date, mealType, [], meals, 'date', 'meal');

                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants, conflictingMeals};
            });

            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            // Show conflict modal if conflicts exist
            if (hasConflicts && onShowConflictModal) {
                trackConflictPrompted(leftovers);
                onShowConflictModal(leftovers, scalingMap, extraServings);
            } else {
                onReturnLeftoverData(leftovers, scalingMap, extraServings);
            }
            closeModal();
        } else {
            // Non-return mode (swap meal flow): check for conflicts before applying
            const leftovers = selected.map(({date, mealType, participants}) => {
                // Note: No need to filter out main meal/existing leftovers since this is for adding/swapping
                const conflictingMeals = findConflictingMeals(date, mealType, [], meals, 'date', 'meal');
                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants, conflictingMeals};
            });
            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            if (hasConflicts && onShowConflictModal) {
                trackConflictPrompted(leftovers);
                onShowConflictModal(leftovers, scalingMap, extraServings);
                closeModal();
            } else {
                onSelectLeftoverOffsets(leftovers, scalingMap, extraServings);
            }
        }
    }

    // How often leftovers land on slots that already have meals, and how much gets
    // overwritten when they do.
    const trackConflictPrompted = (leftovers) => {
        const conflicting = leftovers.filter(leftover => leftover.conflictingMeals.length > 0);

        Analytics.leftoverConflictPrompted({
            'Context': ANALYTICS_CONTEXT,
            'Mode': getLeftoverSchedulerMode(rescheduleMealParams, returnMode),
            'Conflicting Slots': conflicting.length,
            'Conflicting Meals': conflicting.reduce((sum, leftover) => sum + leftover.conflictingMeals.length, 0),
            'Slots Selected': leftovers.length,
        });
    };

    const { dishes, contents } = useMemo(() => getDishes(), [meals, recipes, foods, leftoverParams]);

    // Step = max base servings across all dishes
    const step = dishes.length > 0
        ? Math.max(...dishes.map(({ main: dish }) => {
            const dishContent = contents[dish.recipe_uuid || dish.food_uuid];
            return getBaseServings(dishContent);
        }))
        : 1;

    // Unassigned = actual cooked yield minus allocated (main + selected leftovers) minus stored.
    // Use the scaling returned by getTotalServings (computed from current selections) rather than
    // dish.main.scaling, which is stale when leftover slots are unchecked after being selected.
    const dishServingsList = getTotalServings({ dishes, contents });
    const unassigned = dishServingsList.reduce((sum, { totalServings, scaling }, i) => {
        const dish = dishes[i];
        const dishContent = dish ? contents[dish.main.recipe_uuid || dish.main.food_uuid] : null;
        const baseServings = dishContent ? getBaseServings(dishContent) : 1;
        const actualYield = scaling * baseServings;
        return sum + (actualYield - totalServings);
    }, 0) - extraServings;

    const singleDishTitle =
        leftoverParams.recipe?.title ||
        leftoverParams.mainRecipe?.title ||
        (dishes.length === 1 ? (contents[dishes[0].main.recipe_uuid]?.title || contents[dishes[0].main.food_uuid]?.name || null) : null);

    const isDirty = !returnMode && JSON.stringify(selected) !== JSON.stringify(initialSelectedRef.current);

    const dishLabel = singleDishTitle || 'This meal';
    const rescheduleServingsCount = defaultSelected.reduce((sum, s) =>
        sum + s.participants.reduce((ps, p) => ps + p.portion, 0), 0
    );
    const contextBannerText = !returnMode
        ? rescheduleMealParams
            ? <><strong>{dishLabel} has {rescheduleServingsCount} leftover {rescheduleServingsCount === 1 ? 'serving' : 'servings'} that need to be rescheduled.</strong> Select the meals where you'd like them to go.</>
            : <><strong>{dishLabel} makes {leftoverParams.yieldExcess} extra {leftoverParams.yieldExcess === 1 ? 'serving' : 'servings'}.</strong> Select the meal slots where you'd like to use the leftovers.</>
        : null;

    const analyticsProps = buildLeftoverSchedulerProps({
        context: ANALYTICS_CONTEXT,
        rescheduleMealParams,
        returnMode,
        availableSlots,
        participantDetails,
        preselectedCount: initialSelectedRef.current.length,
        dishTitle: singleDishTitle,
        yieldExcess: leftoverParams.yieldExcess,
    });

    // Fires once per open. The effect body runs after render, so the derived values
    // above are already initialised.
    useEffect(() => {
        Analytics.viewLeftoverScheduler(analyticsProps);
    }, []);

    // Closing without applying anything, as opposed to the "Skip for now" CTA below.
    const onDismiss = () => {
        Analytics.dismissLeftoverScheduler({
            ...analyticsProps,
            'Slots Selected': selected.length,
        });

        closeModal();
    };

    // Mirrors the CTA label exactly: it reads "Skip for now" when nothing changed,
    // which is a declined prompt rather than a scheduled one.
    const trackCtaClick = () => {
        const isSkip = !returnMode && !isDirty;

        if (isSkip) {
            Analytics.skipLeftoverScheduling(analyticsProps);
            return;
        }

        const totalLeftoverServings = selected.reduce((sum, selection) => (
            sum + selection.participants.reduce((participantSum, participant) => participantSum + participant.portion, 0)
        ), 0);

        Analytics.scheduleLeftovers({
            ...analyticsProps,
            'Slots Selected': selected.length,
            'Total Leftover Servings': totalLeftoverServings,
            'Extra Servings': extraServings,
            // Carried for EAT-4022: correlates leftover scheduling with the scaling
            // users actually end up with.
            'Max Scaling': dishServingsList.length ? Math.max(...dishServingsList.map(d => d.scaling)) : null,
            'Was Prefilled': initialSelectedRef.current.length > 0,
            // Whether any slot was assigned to a subset of the household rather than
            // everyone — the outcome that per-person assignment exists to produce.
            'Participants Customized': selected.some(s => s.participants.length !== participantDetails.length),
        });
    };

    return (
        <Modal isOpen={true}
            onRequestClose={onDismiss}
            closeModal={onDismiss}
            contentLabel="Overwrite with leftovers"
            className="el-modal el-modal2 el-modal2-centered leftovers-modal"
            overlayClassName="el-modal-overlay"
            closeTimeoutMS={250}>
            <section className="el-modal-container el-modal2-container leftovers-modal-container">
                <header>
                    <h2>{!returnMode ? 'What should we do with the leftovers?' : 'Leftovers and Batch Cooking'}</h2>
                    {(returnMode || selected.length > 0) && <button className="clear-selections" onClick={() => clearAll()}>Clear all</button>}
                    <button className="el-modal-close-x" onClick={onDismiss}>
                        <i className="icon-close-x" />
                        <span className="assistive-text">Close Modal</span>
                    </button>
                </header>

                <div className="el-modal-body-container el-modal2-body-container el-fonts leftovers-modal-body">
                    {error ? <Alert type="error" description={error} /> : null}
                    {contextBannerText && <Alert type="info" iconType="no-icon" description={contextBannerText} />}

                    {/* Store for Later — standalone card */}
                    {returnMode && (
                        <>
                            <div className="leftovers-section-label">Store unscheduled leftovers</div>
                            <div className="store-bar">
                                <span className="store-bar-label">Servings to store for later</span>
                                <div className="store-bar-controls">
                                    <div className="store-bar-stepper">
                                        <button
                                            className="store-for-later-btn"
                                            onClick={() => setExtraServings(Math.max(0, extraServings - step))}
                                            disabled={extraServings <= 0}
                                        >
                                            -
                                        </button>
                                        <span className="store-for-later-count">{extraServings}</span>
                                        <button
                                            className="store-for-later-btn"
                                            onClick={() => setExtraServings(unassigned > 0 ? extraServings + unassigned : extraServings + step)}
                                        >
                                            +
                                        </button>
                                    </div>
                                    {step > 1 && <div className="store-for-later-step-hint">+/− {step} (min yield)</div>}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="leftovers-section-label schedule-section-label">Schedule leftovers for a specific meal</div>

                    <div className="leftovers-container">
                        {availableSlots.map((slot, slotIndex) => {
                            const { date, mealTypes } = slot;
                            let dateFormat = moment(date).format('ddd, MMM Do');

                            if (moment().subtract(1, 'day').isSame(date, 'day')) {
                                dateFormat = 'Yesterday';
                            } else if (moment().isSame(date, 'day')) {
                                dateFormat = 'Today';
                            } else if (moment().add(1, 'day').isSame(date, 'day')) {
                                dateFormat = 'Tomorrow';
                            }

                            return (
                                <div key={`${date}-${slotIndex}`} className="leftover-date">
                                    <span className="date">
                                        <i className="icon-calendar" />
                                        {dateFormat}
                                    </span>
                                    {mealTypes.map((mealType) => {
                                        const mealTypeDateMeals = meals.filter((m) => m.date === date && m.meal === mealType.name);
                                        const { titles } = getPrimaryMeal(mealTypeDateMeals, recipes, foods);
                                        const hasScheduledMeal = titles.length > 0;
                                        const isLoggedMeal = isSlotLogged(mealTypeDateMeals);
                                        const slotKey = `${date}_${mealType.name}`;
                                        const isExpanded = !!expandedSlots[slotKey];
                                        const selectedCount = getSelectedCount(date, mealType.name);
                                        const isSlotSelected = allParticipantsSelected(date, mealType.name);

                                        return (
                                            <div key={`${dateFormat}_${mealType.name}`} className={`meal-card ${isSlotSelected ? 'is-selected' : ''}`}>
                                                <div className="meal-main">
                                                    <button
                                                        className="el-checkbox meal-check"
                                                        data-active={isSlotSelected}
                                                        onClick={() => selectAllParticipants(date, mealType.name)}
                                                    ></button>
                                                    <div className="meal-info">
                                                        <span className="meal-type-name">{mealType.name}</span>
                                                        {hasScheduledMeal && (
                                                            <span className="meal-dish">
                                                                <span className="scheduled-badge">{isLoggedMeal ? 'Logged' : 'Scheduled'}</span>
                                                                <span className="meal-dish-title">{titles.join(' + ')}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="participants-toggle"
                                                        onClick={() => toggleSlotExpand(date, mealType.name)}
                                                    >
                                                        {selectedCount > 0 && `${selectedCount} ${selectedCount === 1 ? 'person' : 'people'}`}
                                                        <span className={`toggle-arrow ${isExpanded ? 'expanded' : ''}`}>▼</span>
                                                    </button>
                                                </div>
                                                {isExpanded && (
                                                    <div className="participants-panel">
                                                        {participantDetails.map((participant, participantIndex) => {
                                                            const participantIsChecked = isChecked(date, mealType.name, participant.uuid);

                                                            return (
                                                                <div key={`${dateFormat}_${mealType.name}_${participant.name}`} className="participant-container">
                                                                    <button
                                                                        className="el-checkbox"
                                                                        data-active={participantIsChecked}
                                                                        onClick={() => toggleCheckbox(date, mealType.name, participant.uuid)}
                                                                    >
                                                                        <span className="participant-name">
                                                                            {participant.uuid === 'USER' ? (user.first_name || participant.name) : participant.name}
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Guest controls */}
                                                        <div className="participant-container guest-container">
                                                            <div className="guest-info">
                                                                <span className="guest-label">Guests</span>
                                                                <div className="guest-count-controls">
                                                                    <button
                                                                        className="guest-btn"
                                                                        disabled={getCurrentGuestCount(date, mealType.name) <= 0}
                                                                        onClick={() => onChangeNumberOfGuests(date, mealType.name, getCurrentGuestCount(date, mealType.name) - 1)}
                                                                    >
                                                                        −
                                                                    </button>
                                                                    <span className="guest-count">{getCurrentGuestCount(date, mealType.name)}</span>
                                                                    <button
                                                                        className="guest-btn"
                                                                        onClick={() => onChangeNumberOfGuests(date, mealType.name, getCurrentGuestCount(date, mealType.name) + 1)}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <footer>
                    <button className="el-modal-cancel-btn" onClick={onDismiss}>Cancel</button>
                    <button className="el-modal-ok-btn" onClick={() => onClickCta()}>{!returnMode ? (isDirty ? 'Done' : 'Skip for now') : 'Done'}</button>
                </footer>
            </section>
        </Modal>
    );
}

export default LeftoverDatesModal;